import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api.ts';
import { performSync } from '../services/syncService.ts';
import { isBackendOnline } from '../utils/backendStatus.ts';
import { isPWAInstalled } from '../utils/deviceUtils.ts';

// No longer need OFFLINE_MODE flag - hybrid mode handles everything automatically

interface User {
  id: number;
  email: string;
  name: string;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;
  
  // Função para sincronizar dados após login/registro
  const syncAfterLogin = async () => {
    try {
      // Verificar se backend está online antes de sincronizar
      const backendOnline = await isBackendOnline();
      if (!backendOnline) {
        console.log('[AUTH] Backend offline, pulando sincronização após login');
        return;
      }
      
      console.log('[AUTH] Iniciando sincronização após login...');
      const result = await performSync();
      if (result.success) {
        console.log('[AUTH] Sincronização após login concluída com sucesso');
      } else {
        console.warn('[AUTH] Sincronização após login falhou:', result.reason);
      }
    } catch (error) {
      console.error('[AUTH] Erro ao sincronizar após login:', error);
      // Não lança erro - sincronização é opcional e não deve bloquear o login
    }
  };
  
  // Debug log whenever user or isAuthenticated changes
  React.useEffect(() => {
    console.log('[AuthContext] User mudou:', user);
    console.log('[AuthContext] isAuthenticated:', isAuthenticated);
    console.log('[AuthContext] Token no localStorage:', !!localStorage.getItem('token'));
  }, [user, isAuthenticated]);

  useEffect(() => {
    console.log('[AuthContext] useEffect inicial - verificando autenticação...');
    // Check for JWT token (server auth) first
    const token = localStorage.getItem('token');
    console.log('[AuthContext] Token encontrado no localStorage:', !!token);
    if (token) {
      // If we have offline_user, use it immediately (from login or previous session)
      const offline = localStorage.getItem('offline_user');
      if (offline) {
        try {
          const userData = JSON.parse(offline);
          setUser(userData);
          setLoading(false);
          // Verify with server in background (non-blocking)
          api.get('/api/v1/auth/profile')
            .then((response: any) => {
              if (response.data) {
                setUser(response.data);
                // Update offline_user with fresh data
                localStorage.setItem('offline_user', JSON.stringify(response.data));
                // Sincronizar dados do servidor para IndexedDB em background
                syncAfterLogin();
              }
            })
            .catch(() => {
              // Offline or no server - keep current user from offline_user
            });
          return;
        } catch (e) {
          // Invalid offline_user, continue to fetch from server
        }
      }
      
      // No offline_user or invalid, try to get from server
      api.get('/api/v1/auth/profile')
        .then((response: any) => {
          if (response.data) {
            setUser(response.data);
            // Save for offline fallback
            localStorage.setItem('offline_user', JSON.stringify(response.data));
            // Sincronizar dados do servidor para IndexedDB em background
            syncAfterLogin();
          }
        })
        .catch(() => {
          // Offline or no server - DON'T create default user
          // Only allow offline access if we have a valid offline_user
          // This prevents unauthorized access when backend is down
          console.warn('[AUTH] Backend não acessível e sem usuário offline válido');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      // No token - check if offline, if so use offline_user
      // Mas só permitir modo offline se estiver instalado como PWA
      const isOnline = navigator.onLine;
      const isPWA = isPWAInstalled();
      const offline = localStorage.getItem('offline_user');
      
      if (isOnline) {
        // Online but no token - don't clear offline_user immediately
        // It might be needed if user goes offline later
        // Just don't set user - will redirect to login
        // Only clear if explicitly logging out
      } else if (isPWA) {
        // Really offline AND PWA installed - use offline user
        if (offline) {
          try {
            setUser(JSON.parse(offline));
          } catch (e) {
            // Invalid offline_user, create temporary one
            const tempUser = {
              id: 1,
              email: 'offline@device',
              name: 'Offline User',
              is_admin: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            localStorage.setItem('offline_user', JSON.stringify(tempUser));
            setUser(tempUser);
          }
        } else {
          // Offline but no offline_user - create temporary one
          const tempUser = {
            id: 1,
            email: 'offline@device',
            name: 'Offline User',
            is_admin: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          localStorage.setItem('offline_user', JSON.stringify(tempUser));
          setUser(tempUser);
        }
      } else {
        // Offline mas não é PWA - não permitir modo offline
        console.warn('[AUTH] Offline mas não é PWA instalada - modo offline não disponível');
        setUser(null);
      }
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('[AUTH] Iniciando login...');
      const response = await api.post('/api/v1/auth/login', {
        email,
        password
      });

      console.log('[AUTH] Resposta do servidor:', response);
      const { token, user: userData } = response.data;
      
      console.log('[AUTH] Token recebido:', token ? 'SIM' : 'NÃO');
      console.log('[AUTH] User recebido:', userData);
      
      // Save token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('offline_user', JSON.stringify(userData));
      
      console.log('[AUTH] Token salvo no localStorage:', !!localStorage.getItem('token'));
      console.log('[AUTH] Offline user salvo:', !!localStorage.getItem('offline_user'));
      
      // Set user state immediately - this will trigger isAuthenticated to become true
      setUser(userData);
      console.log('[AUTH] User state atualizado');
      console.log('[AUTH] isAuthenticated deve ser:', !!userData);
      
      // Sincronizar dados do servidor para IndexedDB em background (não bloqueia o login)
      syncAfterLogin();
      
      return userData;
    } catch (error) {
      console.error('[AUTH] Erro no login:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        console.error('[AUTH] Erro response:', (error as any).response);
        console.error('[AUTH] Erro response data:', (error as any).response?.data);
      }
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      const response = await api.post('/api/v1/auth/register', {
        name,
        email,
        password
      });

      const { token, user: userData } = response.data;
      
      // Save token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('offline_user', JSON.stringify(userData));
      
      // Set user state immediately
      setUser(userData);
      
      // Sincronizar dados do servidor para IndexedDB em background (não bloqueia o registro)
      syncAfterLogin();
      
      return userData;
    } catch (error) {
      console.error('Erro no registro:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('offline_user');
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    login,
    register,
    logout,
    setUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};